import { groupBy, sortBy } from 'lodash-es'
import type { UseQueryOptions } from '@tanstack/vue-query'
import { useQuery } from '@tanstack/vue-query'
import { minutesToMilliseconds } from 'date-fns'
import type {
  SuggestDestinationsV2Data,
  SuggestDestinationsV2Response,
  SuggestSuggestionV22,
} from '~/types/openapi/search-api'

type SearchQueryOptions<R = SearchResult> = Partial<
  Omit<UseQueryOptions<SearchResult>, 'select'>
> & {
  enabled?: MaybeRefOrGetter<boolean | undefined>
  select?(result: SearchResult, params: SearchApiParams): R
}

export function useRedesignSearchQuery<R = SearchResult>(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: SearchQueryOptions<R> = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()
  const season = useSearchSeason(query)

  const params = useQueryParams(() => ({
    language: context.language,
    currency: context.currency,
    salesoffice: context.salesOfficeCode,
    freeweek: context.isFreeweek || undefined,
    season: season.value,
    ...toValue(query),
  }))

  return useQuery({
    ...(options as Omit<typeof options, 'enabled' | 'select'>),
    placeholderData: (prev: SearchApiResult | undefined) => prev, // Keep previous data between requests
    enabled: computed(
      () => toValue(options.enabled) !== false && import.meta.client,
    ),
    queryKey: ['search-api', 'accomtrips', params] as const,
    queryFn({ queryKey: [_, __, q], signal }) {
      return fetch<SearchApiResult>('/search-api/v1/engines/accomtrips', {
        query: mapSearchApiParams(q),
        signal,
      })
    },
    select(result) {
      const mappedResult = mapSearchResult(context, result, toValue(params))

      return options.select
        ? options.select(mappedResult, toValue(query))
        : mappedResult
    },
  })
}

export function useRedesignMapSearchBoundsQuery(
  area: MaybeRefOrGetter<{ bounds: BoundsValue; zoom: ZoomValue }>,
  filters: MaybeRefOrGetter<MapSearchApiParams>,
  options?: Omit<SearchQueryOptions, 'select'>,
) {
  const settings = useSettings()

  const query = computed(() => ({
    ...toValue(filters),
    ...toValue(area).bounds,
    zoom: toValue(area).zoom,
    pagesize:
      toValue(area).zoom >= settings.mapPricesZoomLevel
        ? SEARCH_API_PAGESIZE_MAX
        : SEARCH_API_PAGESIZE_NONE,
    page: undefined,
    country: undefined,
    region: undefined,
    place: undefined,
  }))

  return useRedesignSearchQuery(query, {
    ...options,
    select: getResultMapItems,
  })
}

export function getResultMapItems(result: SearchResult): MapItem[] {
  const { docs } = result
  const { buckets = [] } = result._aggregations?.karte ?? {}

  return docs.length
    ? getMapItemsFromDocs(docs)
    : getMapItemsFromBuckets(buckets)
}

function getMapItemsFromDocs(docs: SearchResultDocument[]): MapItemAccom[] {
  return Object.values(
    groupBy(
      sortBy(docs, 'trip.price'),
      ({ coords: { lat, lon } }) => lat + lon,
    ),
  ).map((items) => ({
    key: items.map(({ code }) => code).join(),
    type: 'accommodation',
    count: items.length,
    cardinality: 1,
    coords: items[0].coords,
    docs: items,
  }))
}

function getMapItemsFromBuckets(buckets: SearchApiBucket[]): MapItemCluster[] {
  return buckets
    .filter(({ coords, bounds }) => !!coords?.lat && !!coords.lon && !!bounds)
    .map(({ key, count, cardinality, coords, bounds }) => ({
      key,
      type: 'cluster',
      count: count || 1,
      cardinality: cardinality || 1,
      coords: coords!,
      bounds: bounds!,
    }))
}

export function useRedesignSearchApiSuggestionsQuery(
  query: MaybeRefOrGetter<string>,
) {
  const fetch = useRequestFetch()

  const confData = useConfdata()
  const settings = useSettings()

  const params = useQueryParams(
    () =>
      ({
        type: settings.autosuggestSearchtype,
        source: settings.autosuggestSource,
        size: settings.autosuggestSize,
        fuzziness: SUGGESTIONS_FUZZINESS_DEFAULT,
        language: confData.language,
        locale: confData.locale,
        query: toValue(query),
        freeweek: useFlag('freeweek').value,
      }) as SuggestDestinationsV2Data['query'],
  )

  const isQueryValid = computed(() => {
    const { length } = toValue(query)

    return !length || length >= settings.autosuggestThreshold
  })

  return useQuery({
    staleTime: minutesToMilliseconds(SUGGESTIONS_STALE_TIME_MINUTES),
    enabled: isQueryValid,
    placeholderData(previousData?: SuggestDestinationsV2Response) {
      return isQueryValid.value ? previousData : undefined
    },
    queryKey: ['search-suggestions-v2', params] as const,
    queryFn({ queryKey: [_, queryParams], signal }) {
      return fetch<SuggestDestinationsV2Response>('/search-api/v2/suggest', {
        query: queryParams,
        signal,
      })
    },
    select(data) {
      return flatSuggestions<SuggestSuggestionV22>(data?.Suggestions ?? [])
    },
  })
}
